import React, { useEffect, useState, useCallback, useRef } from "react";
import { useApp } from "../context/AppContext";
import api from "../api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Trash2, FileDown, Mail, PenLine, RefreshCw, Upload, FileText, Pencil, Eye, Sparkles, BellRing } from "lucide-react";
import { RichTextEditor } from "./RichTextEditor";
import { EntityLogoUpload } from "./EntityLogoUpload";

export const CATEGORIES = ["contratto", "antiriciclaggio", "privacy"];

const errMsg = (e, t) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : t("msg.error"); };

export const DocTemplatesPanel = ({ category = "" }) => {
  const { t, entity, user } = useApp();
  const [templates, setTemplates] = useState([]);
  const [docs, setDocs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [name, setName] = useState("");
  const [uCat, setUCat] = useState(category || "contratto");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [genTpl, setGenTpl] = useState(null);
  const [genContact, setGenContact] = useState("");
  const [fields, setFields] = useState(null);
  const [values, setValues] = useState({});
  const [generating, setGenerating] = useState(false);
  const [emailDoc, setEmailDoc] = useState(null);
  const [email, setEmail] = useState({ to: "", subject: "", body: "" });
  const [dsDoc, setDsDoc] = useState(null);
  const [signer, setSigner] = useState({ name: "", email: "" });
  const [preview, setPreview] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);
  const [emailPdfUrl, setEmailPdfUrl] = useState("");
  const [dual, setDual] = useState(false);
  const [second, setSecond] = useState({ name: "", email: "" });
  const [entitySettings, setEntitySettings] = useState({});

  useEffect(() => {
    api.get(`/entities/${entity}/settings`).then((r) => setEntitySettings(r.data || {})).catch(() => setEntitySettings({}));
  }, [entity]);

  useEffect(() => {
    if (!emailDoc) return;
    const h = setTimeout(() => {
      api.get(`/docs/generated/${emailDoc.id}/email-preview`, { params: { subject: email.subject, body: email.body } })
        .then((r) => setEmailPreview(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(h);
  }, [emailDoc, email.subject, email.body]);

  const addDefaults = async () => {
    try {
      const r = await api.post(`/docs/templates/defaults${category ? `?category=${category}` : ""}`);
      toast.success(r.data.created.length ? `${t("forms.defaultsAdded")}: ${r.data.created.join(", ")}` : t("forms.defaultsExist"));
      load();
    } catch { toast.error(t("msg.error")); }
  };

  const openPreview = async (d) => {
    try {
      const res = await api.get(`/docs/generated/${d.id}/download`, { responseType: "blob" });
      setPreview({ doc: d, url: URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })) });
    } catch { toast.error(t("msg.error")); }
  };
  const closePreview = () => { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); };

  useEffect(() => { if (category) setUCat(category); }, [category]);

  const load = useCallback(() => {
    api.get("/docs/templates").then((r) => setTemplates(category ? r.data.filter((x) => x.category === category) : r.data)).catch(() => {});
  }, [category]);
  const loadEntity = useCallback(() => {
    api.get(`/docs/generated?entity_id=${entity}${category ? `&category=${category}` : ""}`).then((r) => setDocs(r.data)).catch(() => {});
    api.get(`/contacts?entity_id=${entity}`).then((r) => setContacts(r.data)).catch(() => {});
  }, [entity, category]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadEntity(); }, [loadEntity]);

  const upload = async () => {
    if (!file || !name) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("name", name); fd.append("category", uCat); fd.append("file", file);
      const r = await api.post("/docs/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${t("forms.uploadOk")} — ${t("forms.placeholdersFound")}: ${(r.data.placeholders || []).length}`);
      setName(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(errMsg(e, t)); } finally { setUploading(false); }
  };

  const openEditor = (tpl) => setEditor(tpl
    ? { id: tpl.id, name: tpl.name, category: tpl.category, html: tpl.html_content || "", letterhead: tpl.letterhead !== false }
    : { id: null, name: "", category: uCat, html: "", letterhead: true });

  const saveEditor = async () => {
    if (!editor.name) return;
    setSaving(true);
    try {
      const body = { name: editor.name, category: editor.category, html: editor.html, letterhead: editor.letterhead };
      const r = editor.id ? await api.put(`/docs/templates/${editor.id}/html`, body) : await api.post("/docs/templates/html", body);
      toast.success(`${t("msg.saved")} — ${t("forms.placeholdersFound")}: ${(r.data.placeholders || []).length}`);
      setEditor(null); load();
    } catch (e) { toast.error(errMsg(e, t)); } finally { setSaving(false); }
  };

  const removeTpl = async (id) => {
    try { await api.delete(`/docs/templates/${id}`); toast.success(t("msg.deleted")); load(); } catch { toast.error(t("msg.error")); }
  };

  const openGenerate = (tpl) => { setGenTpl(tpl); setGenContact(""); setFields(null); setValues({}); };
  const loadFields = async () => {
    try {
      const r = await api.get(`/docs/templates/${genTpl.id}/context?contact_id=${genContact}&entity_id=${entity}`);
      setFields(r.data.placeholders); setValues(r.data.values);
    } catch { toast.error(t("msg.error")); }
  };
  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/docs/generate", { template_id: genTpl.id, entity_id: entity, contact_id: genContact, values });
      toast.success(t("forms.generatedOk")); setGenTpl(null); loadEntity();
    } catch (e) { toast.error(errMsg(e, t)); } finally { setGenerating(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/docs/generated/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.filename || "documento.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(t("msg.error")); }
  };

  const openEmail = async (d) => {
    setEmailDoc(d);
    setEmailPreview(null);
    setEmail({ to: contacts.find((x) => x.id === d.contact_id)?.email || "", subject: `${d.template_name} — ${d.contact_name || d.filename}`, body: "" });
    try {
      const res = await api.get(`/docs/generated/${d.id}/download`, { responseType: "blob" });
      setEmailPdfUrl(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })));
    } catch { setEmailPdfUrl(""); }
  };
  const closeEmail = () => { if (emailPdfUrl) URL.revokeObjectURL(emailPdfUrl); setEmailPdfUrl(""); setEmailDoc(null); };
  const sendEmail = async () => {
    try {
      await api.post(`/docs/generated/${emailDoc.id}/email`, email);
      toast.success(t("email.sentOk")); closeEmail(); loadEntity();
    } catch (e) { toast.error(errMsg(e, t)); }
  };

  const openDs = (d) => {
    setDsDoc(d);
    setSigner({ name: d.contact_name || "", email: contacts.find((x) => x.id === d.contact_id)?.email || "" });
    setDual(d.category === "contratto");
    setSecond({ name: entitySettings.seller_name || "", email: user?.email || "" });
  };
  const sendDs = async () => {
    try {
      const r = await api.post(`/docs/generated/${dsDoc.id}/docusign`, {
        signer_name: signer.name, signer_email: signer.email,
        second_signer_name: dual ? second.name : "", second_signer_email: dual ? second.email : "",
      });
      toast.success(`${t("forms.docusignSent")} — ${r.data.status}`); setDsDoc(null); loadEntity();
    } catch (e) { toast.error(errMsg(e, t)); }
  };
  const syncDs = async (d) => {
    try { const r = await api.post(`/docs/generated/${d.id}/docusign/sync`); toast.success(`DocuSign: ${r.data.status}`); loadEntity(); }
    catch (e) { toast.error(errMsg(e, t)); }
  };
  const remindDs = async (d) => {
    try { await api.post(`/docs/generated/${d.id}/docusign/remind`); toast.success(t("forms.remindOk")); loadEntity(); }
    catch (e) { toast.error(errMsg(e, t)); }
  };
  const removeDoc = async (d) => {
    try { await api.delete(`/docs/generated/${d.id}`); toast.success(t("msg.deleted")); loadEntity(); } catch { toast.error(t("msg.error")); }
  };

  return (
    <div className="space-y-6" data-testid={`doc-panel-${category || "all"}`}>
      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-2" data-testid="template-upload-card">
          <CardHeader><CardTitle className="text-base">{t("forms.upload")}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>{t("forms.name")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Contratto di consulenza" data-testid="template-name-input" />
            </div>
            {!category && (
              <div>
                <Label>{t("forms.category")}</Label>
                <Select value={uCat} onValueChange={setUCat}>
                  <SelectTrigger data-testid="template-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`tpl-cat-${c}`}>{t(`forms.cat.${c}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{t("forms.file")}</Label>
              <Input ref={fileRef} type="file" accept=".docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="template-file-input" />
              <p className="text-[11px] text-muted-foreground mt-1">
                {"Nel Word usa segnaposto come {{ragione_sociale}}, {{piva}}, {{codice_fiscale}}, {{indirizzo}}, {{cap}}, {{citta}}, {{provincia}}, {{email}}, {{codice_sdi}}, {{entita}}, {{data}}"}
              </p>
            </div>
            <Button className="w-full" onClick={upload} disabled={uploading || !file || !name} data-testid="template-upload-button">
              <Upload className="h-4 w-4 mr-2" />{uploading ? t("common.loading") : t("forms.upload")}
            </Button>
            <div className="relative py-1 text-center text-[11px] text-muted-foreground"><span className="bg-card px-2 relative z-10">{t("forms.or")}</span><div className="absolute inset-x-0 top-1/2 border-t border-border" /></div>
            <Button variant="outline" className="w-full" onClick={() => openEditor(null)} data-testid="new-html-template-button">
              <FileText className="h-4 w-4 mr-2" />{t("forms.newHtml")}
            </Button>
            <Button variant="ghost" size="sm" className="w-full text-xs" onClick={addDefaults} data-testid="add-default-templates-button">
              <Sparkles className="h-3.5 w-3.5 mr-2" />{t("forms.addDefaults")}
            </Button>
            <div className="border-t border-border pt-3"><EntityLogoUpload entityId={entity} /></div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3" data-testid="templates-list-card">
          <CardHeader><CardTitle className="text-base">{t("forms.templates")}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {templates.length === 0 && <div className="text-sm text-muted-foreground" data-testid="templates-empty">{t("forms.noTemplates")}</div>}
            {templates.map((tpl) => (
              <div key={tpl.id} className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2" data-testid={`doctpl-row-${tpl.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{tpl.name}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="secondary" className="text-[10px]">{t(`forms.cat.${tpl.category}`)}</Badge>
                    <Badge variant="outline" className="text-[10px]" data-testid={`tpl-kind-${tpl.id}`}>{tpl.kind === "html" ? t("forms.kind.html") : "Word"}</Badge>
                    {tpl.original_filename && <span className="text-[10px] text-muted-foreground font-mono truncate">{tpl.original_filename}</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" data-testid={`generate-tpl-${tpl.id}`} onClick={() => openGenerate(tpl)}>{t("forms.generate")}</Button>
                  {tpl.kind === "html" && (
                    <Button size="icon" variant="ghost" data-testid={`edit-tpl-${tpl.id}`} onClick={() => openEditor(tpl)}><Pencil className="h-4 w-4" /></Button>
                  )}
                  <Button size="icon" variant="ghost" data-testid={`delete-tpl-${tpl.id}`} onClick={() => removeTpl(tpl.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card data-testid="generated-docs-card">
        <CardHeader><CardTitle className="text-base">{t("forms.archive")}</CardTitle></CardHeader>
        <CardContent>
          {docs.length === 0 ? <div className="text-sm text-muted-foreground" data-testid="docs-empty">{t("forms.noDocs")}</div> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>{t("forms.contact")}</TableHead>
                  <TableHead>{t("forms.category")}</TableHead>
                  <TableHead>{t("common.date")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead>{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                    <TableCell className="text-sm max-w-[220px] truncate">{d.filename}</TableCell>
                    <TableCell className="text-sm">{d.contact_name || "—"}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-[10px]">{t(`forms.cat.${d.category}`)}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{(d.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell>
                      <Badge variant={d.status === "generated" ? "secondary" : "outline"} className="text-[10px]" data-testid={`doc-status-${d.id}`}>
                        {t(`forms.status.${d.status}`)}{d.docusign_status && d.status === "docusign_sent" ? `: ${d.docusign_status}` : ""}
                      </Badge>
                      {d.signed_vault_id && <Badge variant="outline" className="text-[10px] ml-1 text-emerald-500 border-emerald-500/30" title={t("forms.signedCopy")} data-testid={`doc-signed-${d.id}`}>Vault ✓</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" title={t("forms.preview")} data-testid={`preview-doc-${d.id}`} onClick={() => openPreview(d)}><Eye className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" title={t("forms.download")} data-testid={`download-doc-${d.id}`} onClick={() => download(d)}><FileDown className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" title={t("forms.email")} data-testid={`email-doc-${d.id}`} onClick={() => openEmail(d)}><Mail className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" title={t("forms.docusign")} data-testid={`docusign-doc-${d.id}`} onClick={() => openDs(d)}><PenLine className="h-4 w-4" /></Button>
                        {d.status === "docusign_sent" && (
                          <Button size="icon" variant="ghost" title={t("forms.sync")} data-testid={`sync-doc-${d.id}`} onClick={() => syncDs(d)}><RefreshCw className="h-4 w-4" /></Button>
                        )}
                        {d.status === "docusign_sent" && !["completed", "declined", "voided"].includes(d.docusign_status) && (
                          <Button size="icon" variant="ghost" title={t("forms.remind")} data-testid={`remind-doc-${d.id}`} onClick={() => remindDs(d)}><BellRing className="h-4 w-4" /></Button>
                        )}
                        <Button size="icon" variant="ghost" data-testid={`delete-doc-${d.id}`} onClick={() => removeDoc(d)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!preview} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col" data-testid="pdf-preview-dialog">
          <DialogHeader><DialogTitle className="truncate">{t("forms.preview")}: {preview?.doc?.filename}</DialogTitle></DialogHeader>
          {preview && (<>
            <iframe title="pdf" src={preview.url} className="flex-1 w-full rounded-md border border-border bg-white" data-testid="pdf-preview-frame" />
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={() => download(preview.doc)} data-testid="preview-download-button"><FileDown className="h-4 w-4 mr-2" />{t("forms.download")}</Button>
              <Button variant="outline" onClick={() => { const d = preview.doc; closePreview(); openEmail(d); }} data-testid="preview-email-button"><Mail className="h-4 w-4 mr-2" />{t("forms.email")}</Button>
              <Button onClick={() => { const d = preview.doc; closePreview(); openDs(d); }} data-testid="preview-docusign-button"><PenLine className="h-4 w-4 mr-2" />{t("forms.docusign")}</Button>
            </div>
          </>)}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="html-template-dialog">
          <DialogHeader><DialogTitle>{editor?.id ? t("forms.editHtml") : t("forms.newHtml")}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <Label>{t("forms.name")}</Label>
                  <Input value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} data-testid="html-template-name-input" />
                </div>
                <div>
                  <Label>{t("forms.category")}</Label>
                  <Select value={editor.category} onValueChange={(v) => setEditor({ ...editor, category: v })}>
                    <SelectTrigger data-testid="html-template-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`forms.cat.${c}`)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <RichTextEditor value={editor.html} onChange={(html) => setEditor((ed) => ({ ...ed, html }))} placeholderLabel={t("forms.insertPlaceholder")} />
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={editor.letterhead} onChange={(e) => setEditor({ ...editor, letterhead: e.target.checked })} data-testid="html-template-letterhead-checkbox" />
                {t("forms.letterhead")}
              </label>
              <Button className="w-full" onClick={saveEditor} disabled={saving || !editor.name} data-testid="html-template-save-button">
                {saving ? t("common.loading") : t("forms.saveTemplate")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!genTpl} onOpenChange={(v) => !v && setGenTpl(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("forms.generateTitle")}: {genTpl?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("forms.contact")}</Label>
              <Select value={genContact} onValueChange={setGenContact}>
                <SelectTrigger data-testid="generate-contact-select"><SelectValue placeholder={t("forms.noContact")} /></SelectTrigger>
                <SelectContent>
                  {contacts.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`contact-option-${c.id}`}>{c.ragione_sociale || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" className="w-full" onClick={loadFields} data-testid="load-fields-button">{t("forms.loadFields")}</Button>
            {fields && (
              <div className="space-y-3" data-testid="generate-fields">
                <Label>{t("forms.fields")}</Label>
                {fields.length === 0 && <p className="text-xs text-muted-foreground">—</p>}
                {fields.map((f) => (
                  <div key={f}>
                    <Label className="text-xs font-mono">{`{{${f}}}`}</Label>
                    <Input value={values[f] || ""} onChange={(e) => setValues({ ...values, [f]: e.target.value })} data-testid={`field-${f}`} />
                  </div>
                ))}
              </div>
            )}
            <Button className="w-full" onClick={generate} disabled={generating || !fields} data-testid="generate-pdf-button">
              {generating ? t("common.loading") : t("forms.generatePdf")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailDoc} onOpenChange={(v) => !v && closeEmail()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="doc-email-dialog">
          <DialogHeader><DialogTitle>{t("forms.email")}: {emailDoc?.filename}</DialogTitle></DialogHeader>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div><Label>{t("email.to")}</Label>
                <Input type="email" value={email.to} onChange={(e) => setEmail({ ...email, to: e.target.value })} data-testid="doc-email-to-input" /></div>
              <div><Label>{t("email.subject")}</Label>
                <Input value={email.subject} onChange={(e) => setEmail({ ...email, subject: e.target.value })} data-testid="doc-email-subject-input" /></div>
              <div><Label>{t("email.body")}</Label>
                <textarea className="flex min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={email.body} onChange={(e) => setEmail({ ...email, body: e.target.value })} placeholder={t("forms.emailBodyHint")} data-testid="doc-email-body-input" /></div>
              <Button className="w-full" onClick={sendEmail} disabled={!email.to} data-testid="doc-email-send-button">{t("email.send")}</Button>
            </div>
            <div className="space-y-3" data-testid="doc-email-preview">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t("forms.emailPreview")}</Label>
              <div className="rounded-md border border-border bg-muted/30 p-3 text-sm space-y-2">
                <div className="text-xs text-muted-foreground">{t("email.to")}: <span className="text-foreground">{email.to || "—"}</span></div>
                <div className="font-medium" data-testid="doc-email-preview-subject">{emailPreview?.subject || email.subject}</div>
                <div className="whitespace-pre-wrap text-sm border-t border-border pt-2" data-testid="doc-email-preview-body">{emailPreview?.body || "…"}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1 border-t border-border pt-2"><FileDown className="h-3 w-3" />{t("email.attachments")}: {emailDoc?.filename}</div>
              </div>
              {emailPdfUrl && <iframe title="pdf-attach" src={emailPdfUrl} className="w-full h-[320px] rounded-md border border-border bg-white" data-testid="doc-email-pdf-frame" />}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dsDoc} onOpenChange={(v) => !v && setDsDoc(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("forms.docusign")}: {dsDoc?.filename}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t("forms.signerName")}</Label>
              <Input value={signer.name} onChange={(e) => setSigner({ ...signer, name: e.target.value })} data-testid="signer-name-input" /></div>
            <div><Label>{t("forms.signerEmail")}</Label>
              <Input type="email" value={signer.email} onChange={(e) => setSigner({ ...signer, email: e.target.value })} data-testid="signer-email-input" /></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={dual} onChange={(e) => setDual(e.target.checked)} data-testid="dual-signer-checkbox" />
              {t("forms.dualSign")}
            </label>
            {dual && (
              <div className="grid grid-cols-2 gap-3 rounded-md border border-border p-3" data-testid="second-signer-fields">
                <div><Label className="text-xs">{t("forms.secondSignerName")}</Label>
                  <Input value={second.name} onChange={(e) => setSecond({ ...second, name: e.target.value })} data-testid="second-signer-name-input" /></div>
                <div><Label className="text-xs">{t("forms.secondSignerEmail")}</Label>
                  <Input type="email" value={second.email} onChange={(e) => setSecond({ ...second, email: e.target.value })} data-testid="second-signer-email-input" /></div>
                <p className="col-span-2 text-[11px] text-muted-foreground">{t("forms.dualSignHint")}</p>
              </div>
            )}
            <Button className="w-full" onClick={sendDs} disabled={!signer.name || !signer.email || (dual && !second.email)} data-testid="docusign-send-button">{t("forms.docusign")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
